import {UnstyledButton, createStyles} from '@mantine/core';
import React from 'react';

export interface Tab<T extends string = string> {
  id: T;
  label: string;
  icon?: React.ReactNode;
}

const useStyles = createStyles(() => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '8px 16px',
    borderBottom: '1px solid #e9ecef',
  },
}));

interface FilterTabsProps<T extends string> {
  activeTab: T;
  onTabChange: (tab: T) => void;
  tabs: Array<Tab<T>>;
  className?: string;
}

export default function FilterTabs<T extends string>({
  activeTab,
  onTabChange,
  tabs,
  className,
}: FilterTabsProps<T>): JSX.Element {
  const {classes} = useStyles();
  return (
    <div role="tablist" className={className ?? classes.root}>
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <UnstyledButton
            key={tab.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => {
              onTabChange(tab.id);
            }}
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 12px',
              borderRadius: theme.radius.xl,
              fontSize: 13,
              fontWeight: isActive ? 600 : 400,
              color: isActive ? theme.colors.blue[7] : theme.colors.gray[7],
              backgroundColor: isActive ? theme.colors.blue[0] : 'transparent',
              transition: 'all 100ms ease',
              '&:hover': {
                backgroundColor: isActive ? theme.colors.blue[0] : theme.colors.gray[0],
              },
            })}
          >
            {tab.icon}
            {tab.label}
          </UnstyledButton>
        );
      })}
    </div>
  );
}
